import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  Stethoscope, 
  Code, 
  Wrench, 
  Utensils, 
  Building2, 
  Truck, 
  GraduationCap, 
  Users, 
  Check, 
  ArrowRight, 
  Star,
  Briefcase,
  Home,
  FileText,
  Heart,
  Globe,
  Target,
  MessagesSquare,
  Sparkles
} from 'lucide-react';
import { CONVERSATIONS } from '../data/conversations';

const TOTAL_CONVOS = Object.values(CONVERSATIONS).reduce((n, arr) => n + arr.filter((t) => t.questions.length > 0).length, 0);

const LandingPage = () => {
  const { user } = useAuth();

  const professions = [
    {
      icon: <Stethoscope className="w-8 h-8" />,
      title: 'Healthcare & Nursing',
      titleDe: 'Gesundheit & Pflege',
      description: 'Medical terminology, patient communication, hospital procedures, and healthcare documentation.',
      jobs: ['Nurse', 'Doctor', 'Caregiver', 'Medical Assistant'],
      color: 'from-red-400 to-red-500',
      bgColor: 'bg-red-50',
      iconColor: 'text-red-500',
      path: '/careers/healthcare'
    },
    {
      icon: <Code className="w-8 h-8" />,
      title: 'IT & Technology',
      titleDe: 'IT & Technologie',
      description: 'Technical vocabulary, team communication, agile terminology, and professional emails.',
      jobs: ['Software Developer', 'IT Support', 'Data Analyst', 'Project Manager'],
      color: 'from-blue-400 to-blue-500',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-500',
      path: '/careers/it'
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: 'Engineering & Trades',
      titleDe: 'Ingenieurwesen & Handwerk',
      description: 'Technical terms, safety instructions, blueprints, and workplace communication.',
      jobs: ['Engineer', 'Electrician', 'Mechanic', 'Technician'],
      color: 'from-orange-400 to-orange-500',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-500',
      path: '/careers/engineering'
    },
    {
      icon: <Utensils className="w-8 h-8" />,
      title: 'Hospitality & Gastronomy',
      titleDe: 'Gastronomie & Hotel',
      description: 'Customer service, menu vocabulary, reservations, and hospitality phrases.',
      jobs: ['Chef', 'Waiter', 'Hotel Staff', 'Barista'],
      color: 'from-amber-400 to-amber-500',
      bgColor: 'bg-amber-50',
      iconColor: 'text-amber-500',
      path: '/careers/hospitality'
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: 'Business & Office',
      titleDe: 'Büro & Verwaltung',
      description: 'Business German, email writing, meetings, presentations, and office communication.',
      jobs: ['Office Manager', 'Accountant', 'HR', 'Sales'],
      color: 'from-blue-400 to-blue-500',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-500',
      path: '/careers/business'
    },
    {
      icon: <Truck className="w-8 h-8" />,
      title: 'Logistics & Transport',
      titleDe: 'Logistik & Transport',
      description: 'Delivery vocabulary, route planning, warehouse terms, and customer interaction.',
      jobs: ['Driver', 'Warehouse Worker', 'Dispatcher', 'Freight Handler'],
      color: 'from-emerald-400 to-emerald-500',
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-500',
      path: '/careers/logistics'
    }
  ];

  const integrationModules = [
    {
      icon: <Home className="w-6 h-6 text-primary" />,
      title: 'Finding Housing',
      titleDe: 'Wohnungssuche',
      description: 'Rental applications, Schufa, contracts, Kalt- vs. Warmmiete and housing vocabulary.',
      path: '/integration/housing'
    },
    {
      icon: <FileText className="w-6 h-6 text-primary" />,
      title: 'Visa & Immigration',
      titleDe: 'Visum & Einwanderung',
      description: 'Work Visa, EU Blue Card, Chancenkarte, Student Visa, family reunification — full guide.',
      path: '/integration/visa'
    },
    {
      icon: <Heart className="w-6 h-6 text-primary" />,
      title: 'Healthcare System',
      titleDe: 'Gesundheitssystem',
      description: 'Doctor visits, insurance, pharmacy, and medical appointments.',
      path: '/integration/healthcare-system'
    },
    {
      icon: <Briefcase className="w-6 h-6 text-primary" />,
      title: 'Opening a Business',
      titleDe: 'Unternehmensgründung',
      description: 'Kleinunternehmer, GmbH, Gewerbeanmeldung — the German legal entity guide.',
      path: '/integration/business-setup'
    },
    {
      icon: <Users className="w-6 h-6 text-primary" />,
      title: 'Daily Life & Culture',
      titleDe: 'Alltag & Kultur',
      description: 'How Germans think and communicate — navigate daily life like a local.',
      path: '/integration/daily-life'
    },
    {
      icon: <GraduationCap className="w-6 h-6 text-primary" />,
      title: 'Education & Training',
      titleDe: 'Bildung & Ausbildung',
      description: 'School system, Ausbildung, university, and professional certifications.',
      path: '/integration/education'
    }
  ];

  const benefits = [
    { icon: <Target className="w-5 h-5" />, text: 'Job-specific vocabulary from day one' },
    { icon: <Globe className="w-5 h-5" />, text: 'Real-world integration scenarios' },
    { icon: <Briefcase className="w-5 h-5" />, text: 'Workplace communication skills' },
    { icon: <Users className="w-5 h-5" />, text: 'Cultural insights for success' }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white">
      <Navbar />

      {/* Hero Section */}
      <section className="py-10 md:py-14 px-6 relative overflow-hidden" data-testid="hero-section">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-babyblue-300/10 rounded-full filter blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Main Logo Text */}
            <div className="mb-5">
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-primary tracking-tight">
                Germanatic
              </h1>
            </div>

            <div className="mb-6 max-w-2xl mx-auto" data-testid="home-intro">
              <p className="font-heading text-2xl md:text-3xl font-extrabold text-babyblue-900 tracking-tight" data-testid="home-intro-tagline">
                Lerne richtiges Deutsch und keinen Bullshit
              </p>
            </div>

            <p className="text-base text-babyblue-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              Fast-track your integration into Germany. Learn job-specific German vocabulary, workplace communication, and everything you need to succeed in your new career and life.
            </p>

            {/* Benefits */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2 bg-white/80 px-3 py-1.5 rounded-full shadow-sm border border-babyblue-200">
                  <span className="text-primary">{benefit.icon}</span>
                  <span className="text-babyblue-700 text-xs font-medium">{benefit.text}</span>
                </div>
              ))}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {user ? (
                <Link to="/dashboard" className="btn-primary text-center inline-flex items-center justify-center gap-2" data-testid="get-started-button">
                  Go to Dashboard
                  <ArrowRight className="w-5 h-5" />
                </Link>
              ) : (
                <Link to="/convos" className="btn-primary text-center inline-flex items-center justify-center gap-2" data-testid="get-started-button">
                  Start Learning
                  <ArrowRight className="w-5 h-5" />
                </Link>
              )}
            </div>
            
            <p className="text-xs text-babyblue-600 mt-4 flex items-center justify-center gap-2">
              <Check className="w-4 h-4 text-green-500" />
              100% Free - Start your integration journey today
            </p>
          </div>
        </div>
      </section>

      {/* Conversation Kickstarters Section */}
      <section className="py-10 md:py-14 px-6 bg-gradient-to-br from-babyblue-50 via-white to-babyblue-100" data-testid="convos-hero-section">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              <MessagesSquare className="w-4 h-4" />
              Germanatic Convos
            </div>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-babyblue-800 mb-4">
              Conversation Kickstarters
            </h2>
            <div className="relative inline-block mb-4">
              <div className="font-heading text-5xl md:text-7xl lg:text-8xl font-extrabold bg-gradient-to-br from-primary via-babyblue-500 to-babyblue-700 bg-clip-text text-transparent tracking-tight leading-none">
                {TOTAL_CONVOS}+
              </div>
              <Sparkles className="absolute -top-2 -right-6 w-7 h-7 text-primary animate-pulse" />
            </div>
            <p className="text-base md:text-lg text-babyblue-700 max-w-2xl mx-auto mb-6">
              Themen von A bis Z — sprich mit deinem Sprachpartner oder nimm dich selbst auf.
              <br />
              <span className="text-babyblue-500 italic text-sm">
                Topics from A to Z — talk with your language partner or record yourself answering.
              </span>
            </p>
            <Link
              to="/convos"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="explore-convos-button"
            >
              Themen entdecken / Explore topics
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Professional Paths Section */}
      <section className="py-20 md:py-24 px-6 bg-white" data-testid="professions-section">
        <div className="container mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block bg-primary/10 text-primary px-4 py-1 rounded-full text-sm font-medium mb-4">
              Career-Focused Learning
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-babyblue-800 mb-4">
              Choose Your Professional Path
            </h2>
            <p className="text-lg text-babyblue-600 max-w-2xl mx-auto">
              Learn German tailored to your industry. Get the vocabulary and communication skills you need for your specific job.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {professions.map((profession, index) => (
              <Link 
                key={index} 
                to={profession.path}
                className="card hover:shadow-xl group cursor-pointer" 
                data-testid={`profession-card-${index}`}
              >
                <div className={`w-14 h-14 ${profession.bgColor} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <span className={profession.iconColor}>{profession.icon}</span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-babyblue-800 mb-1">
                  {profession.title}
                </h3>
                <p className="text-sm text-primary font-medium mb-2">{profession.titleDe}</p>
                <p className="text-babyblue-600 leading-relaxed mb-4 text-sm">
                  {profession.description}
                </p>
                <div className="mt-4 flex items-center text-primary font-medium text-sm group-hover:gap-2 transition-all">
                  Start Learning <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-20 md:py-24 px-6 bg-gradient-to-b from-babyblue-50 to-white" data-testid="integration-section">
        <div className="container mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-100 text-green-700 px-4 py-1 rounded-full text-sm font-medium mb-4">
              Complete Integration Support
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-babyblue-800 mb-4">
              More Than Just Language
            </h2>
            <p className="text-lg text-babyblue-600 max-w-2xl mx-auto">
              We help you navigate life in Germany. From finding an apartment to understanding the healthcare system.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {integrationModules.map((module, index) => (
              <Link
                key={index}
                to={module.path}
                className="bg-white border border-babyblue-200 rounded-2xl p-6 hover:shadow-lg hover:border-primary/40 transition-all block group"
                data-testid={`integration-card-${index}`}
              >
                <div className="w-12 h-12 bg-babyblue-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  {module.icon}
                </div>
                <h3 className="font-heading text-lg font-semibold text-babyblue-800 mb-1">
                  {module.title}
                </h3>
                <p className="text-sm text-primary font-medium mb-2">{module.titleDe}</p>
                <p className="text-babyblue-600 text-sm leading-relaxed">
                  {module.description}
                </p>
                <div className="mt-3 flex items-center text-primary font-medium text-sm group-hover:gap-2 transition-all">
                  Explore <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 md:py-24 px-6 bg-white" data-testid="how-it-works-section">
        <div className="container mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-babyblue-800 mb-4">
              Your Path to Integration
            </h2>
            <p className="text-lg text-babyblue-600 max-w-2xl mx-auto">
              A structured approach to learning German and settling into your new life.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-primary to-babyblue-400 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  1
                </div>
                <h3 className="font-heading text-xl font-semibold text-babyblue-800 mb-2">Choose Your Path</h3>
                <p className="text-babyblue-600">Select your profession or integration goal to get personalized content.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-primary to-babyblue-400 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  2
                </div>
                <h3 className="font-heading text-xl font-semibold text-babyblue-800 mb-2">Learn & Practice</h3>
                <p className="text-babyblue-600">Study job-specific vocabulary, phrases, and real-world scenarios.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-primary to-babyblue-400 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  3
                </div>
                <h3 className="font-heading text-xl font-semibold text-babyblue-800 mb-2">Succeed in Germany</h3>
                <p className="text-babyblue-600">Apply your skills at work and integrate smoothly into German society.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-24 px-6 bg-gradient-to-r from-primary to-babyblue-400" data-testid="cta-section">
        <div className="container mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Your New Life in Germany?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Your journey starts with the right visa. Learn everything about the Work Visa, EU Blue Card, Chancenkarte, Student Visa and family reunification — all in one place.
          </p>
          <Link to="/integration/visa" className="bg-white text-primary hover:bg-babyblue-50 px-8 py-3 rounded-full font-semibold inline-flex items-center gap-2 transition-all shadow-lg" data-testid="cta-visa-button">
            Explore the Visa Guide
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Reviews Preview Section */}
      <section className="py-20 md:py-24 px-6 bg-white" data-testid="reviews-section">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-babyblue-800 mb-4">
              Success Stories
            </h2>
            <p className="text-lg text-babyblue-600">
              Hear from professionals who successfully integrated into Germany
            </p>
          </div>
          <div className="text-center">
            <Link to="/reviews" className="btn-primary inline-flex items-center gap-2">
              View All Stories
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
